import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ConfirmModal } from '../../components/settings/ConfirmModal';
import { PurchaseTile } from '../../components/me/PurchaseTile';
import { useMyPurchases } from '../../hooks/useMyPurchases';
import { useDeletePurchase } from '../../hooks/useDeletePurchase';
import type { PurchaseItem } from '../../hooks/useMyPurchases';
import { resultRoute } from '../../lib/result-route';
import tile from '../../components/me/PurchaseTile.module.css';
import styles from './Purchases.module.css';

type DeleteVariant = 'permanent' | 'couple' | 'natal' | 'compat';

/** Mirror of the server categorize(), for confirm copy only. */
function deleteVariant(item: PurchaseItem): DeleteVariant {
  switch (item.result?.kind) {
    case 'couple':
      return 'couple';
    case 'natal':
      return 'natal';
    case 'compat':
      return 'compat';
    default:
      return 'permanent'; // avatar, past_lives, text, or no viewer
  }
}

export function Purchases(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['me', 'common']);
  const { data, isLoading, isError } = useMyPurchases();
  const deletePurchase = useDeletePurchase();

  const [pendingDelete, setPendingDelete] = useState<PurchaseItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function openItem(item: PurchaseItem): void {
    void navigate(resultRoute(item.result) ?? `/sku/${item.skuCode}`);
  }

  async function handleDeleteConfirm(): Promise<void> {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;
    setNotice(null);
    try {
      await deletePurchase.mutateAsync(target.id);
    } catch (e) {
      setNotice(
        (e as Error).message === 'in_flight'
          ? t('me:purchases.deleteInFlight')
          : t('me:purchases.loadError'),
      );
    }
  }

  const variant: DeleteVariant = pendingDelete ? deleteVariant(pendingDelete) : 'permanent';

  return (
    <MeSubpageShell title={t('me:purchases.title')} data-testid="purchases-page">
      {notice && (
        <p role="status" className={styles.meta} data-testid="purchases-notice">
          {notice}
        </p>
      )}
      {isLoading && (
        <p className={styles.meta} data-testid="purchases-loading">
          {t('common:states.loading')}
        </p>
      )}
      {isError && (
        <p role="alert" data-testid="purchases-error">
          {t('me:purchases.loadError')}
        </p>
      )}
      {!isLoading && !isError && data && data.length === 0 && (
        <div className={styles.emptyState} data-testid="purchases-empty-state">
          <span className={styles.emptyGlyph}>✦</span>
          <p className={styles.emptyText}>{t('me:purchases.noRites')}</p>
        </div>
      )}
      {!isLoading && !isError && data && data.length > 0 && (
        <div className={tile.grid} data-testid="purchases-list">
          {data.map((item, idx) => (
            <PurchaseTile
              key={item.id}
              item={item}
              onClick={() => openItem(item)}
              onDelete={() => setPendingDelete(item)}
              deleteAriaLabel={t('me:purchases.deleteAriaLabel')}
              cardTestId={`purchases-card-${idx}`}
              deleteTestId={`purchases-delete-${idx}`}
            />
          ))}
        </div>
      )}

      {pendingDelete !== null && (
        <ConfirmModal
          title={t(`me:purchases.deleteConfirm.${variant}.title`)}
          body={t(`me:purchases.deleteConfirm.${variant}.body`)}
          confirmLabel={t(
            variant === 'permanent'
              ? 'me:purchases.deleteConfirmDelete'
              : 'me:purchases.deleteConfirmRemove',
          )}
          cancelLabel={t('me:purchases.deleteConfirmCancel')}
          variant="danger"
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => setPendingDelete(null)}
          data-testid="purchases-delete-confirm"
        />
      )}
    </MeSubpageShell>
  );
}
